/**
 * Local persistence — settings, personal bests, and the offline score log.
 *
 * Everything degrades quietly: private-mode Safari throws on localStorage, and
 * a game that crashes because it couldn't save a preference is a bad game.
 */

import type { ModeId, RunSummary } from './core/types'

const KEY = 'ouroboros.v2'

export interface Settings {
  name: string
  muted: boolean
  music: boolean
  reducedMotion: boolean
  showLattice: boolean
  showScanlines: boolean
  daltonize: boolean
  lastMode: ModeId
  seenTutorial: boolean
}

export interface LocalScore extends RunSummary {
  at: number
  name: string
}

interface Saved {
  settings: Settings
  best: Partial<Record<ModeId, number>>
  scores: LocalScore[]
  /** Runs that couldn't reach the server; retried on next load. */
  pending: unknown[]
}

const DEFAULTS: Settings = {
  name: '',
  muted: false,
  music: true,
  reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  showLattice: true,
  showScanlines: false,
  daltonize: false,
  lastMode: 'ouroboros',
  seenTutorial: false,
}

function read(): Saved {
  const blank: Saved = { settings: { ...DEFAULTS }, best: {}, scores: [], pending: [] }
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return blank
    const parsed = JSON.parse(raw) as Partial<Saved>
    return {
      settings: { ...DEFAULTS, ...(parsed.settings ?? {}) },
      best: parsed.best ?? {},
      scores: parsed.scores ?? [],
      pending: parsed.pending ?? [],
    }
  } catch {
    return blank
  }
}

function write(data: Saved): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(data))
  } catch {
    /* storage unavailable — the session still plays fine */
  }
}

let cache = read()

export const store = {
  get settings(): Settings {
    return cache.settings
  },

  saveSettings(patch: Partial<Settings>): Settings {
    cache.settings = { ...cache.settings, ...patch }
    write(cache)
    return cache.settings
  },

  bestFor(mode: ModeId): number {
    return cache.best[mode] ?? 0
  },

  /** Records a finished run. Returns true if it beat the personal best. */
  recordRun(run: RunSummary, name: string): boolean {
    const improved = run.score > (cache.best[run.mode] ?? 0)
    if (improved) cache.best[run.mode] = run.score
    // A run worth nothing is history, not a score — keeping it just pads the
    // local board with zeroes.
    if (run.score <= 0) return false
    cache.scores.push({ ...run, at: Date.now(), name })
    cache.scores.sort((a, b) => b.score - a.score)
    cache.scores = cache.scores.slice(0, 50)
    write(cache)
    return improved
  },

  localTop(mode: ModeId, limit = 20): LocalScore[] {
    return cache.scores.filter((s) => s.mode === mode).slice(0, limit)
  },

  get pending(): unknown[] {
    return cache.pending
  },

  queuePending(payload: unknown): void {
    cache.pending.push(payload)
    cache.pending = cache.pending.slice(-10)
    write(cache)
  },

  clearPending(): void {
    cache.pending = []
    write(cache)
  },

  reset(): void {
    cache = { settings: { ...DEFAULTS }, best: {}, scores: [], pending: [] }
    write(cache)
  },
}
